import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  MessageCircle, ShieldCheck, Mic, ArrowRight, Hotel, Users,
  Sparkles, MapPin, Calendar, Building2, Flame, Coins, Share2,
  Leaf, Scale, CircleDot, KeyRound,
} from 'lucide-react';
import { seed, hotels as hotelsApi } from '@/lib/api';
import '@/App.css';
import { BACKEND_URL } from '@/lib/backendUrl';
import SEO from '@/components/seo/SEO';

/* Premium hotel room — atmospheric, warm, human (Unsplash, licensed) */
const HERO_PHOTO = 'https://images.unsplash.com/photo-1566665797739-1674de7a421a?w=1600&q=80&auto=format&fit=crop';

/* ─────────────────────────  NAV  ───────────────────────── */
function LobbyNav() {
  return (
    <nav
      className="sticky top-0 z-50 border-b border-[color:var(--l-line)] bg-[color:var(--l-bg)]/85 backdrop-blur-md"
      data-testid="landing-nav"
    >
      <div className="max-w-[1320px] mx-auto px-5 sm:px-8 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="nav-logo">
          <div className="w-8 h-8 rounded-full bg-[color:var(--l-copper)] flex items-center justify-center">
            <span className="l-display text-[color:var(--l-bg)] text-sm">b</span>
          </div>
          <span className="l-display text-[17px] tracking-tight">getbitsy</span>
        </Link>

        <div className="hidden md:flex items-center gap-7 text-sm text-[color:var(--l-cream-2)]">
          <Link to="/marketplace" className="hover:text-[color:var(--l-cream)] transition-colors">Stays</Link>
          <Link to="/bounty-board" className="hover:text-[color:var(--l-cream)] transition-colors" data-testid="nav-bounty-board">Rewards</Link>
          <Link to="/guest-login" className="hover:text-[color:var(--l-cream)] transition-colors" data-testid="nav-hotelogram">Hotelogram</Link>
          <Link to="/why-getbitsy" className="hover:text-[color:var(--l-cream)] transition-colors" data-testid="nav-why-getbitsy">How it works</Link>
        </div>

        <div className="flex items-center gap-2">
          <Link to="/login" data-testid="landing-hotel-owner-cta-button">
            <button className="l-btn-ghost h-10 px-5 text-sm">For hotels</button>
          </Link>
        </div>
      </div>
    </nav>
  );
}

/* ─────────────────────────  HERO  ───────────────────────── */
function Hero({ onStart }) {
  const [draft, setDraft] = useState('');

  return (
    <section className="relative overflow-hidden" data-testid="landing-hero">
      <div className="absolute inset-0 lobby-glow pointer-events-none" />
      <div className="lobby-grain" />

      <div className="max-w-[1320px] mx-auto px-5 sm:px-8 lg:px-10 py-14 sm:py-20 lg:py-28 relative z-10">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">

          {/* Left — the statement */}
          <div className="lg:col-span-7">
            <div className="lobby-reveal flex items-center gap-3 mb-8 text-[13px] text-[color:var(--l-cream-3)]">
              <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--l-copper)]" />
              <span className="tracking-wide">A new way to stay</span>
            </div>

            <h1
              className="l-display lobby-reveal d1 text-[clamp(2.6rem,7.2vw,6.2rem)] text-[color:var(--l-cream)]"
              data-testid="hero-headline"
            >
              Don&rsquo;t book a hotel.
              <br />
              Start a <span className="text-[color:var(--l-copper)]">conversation</span>.
            </h1>

            <p className="lobby-reveal d2 mt-8 text-lg sm:text-xl text-[color:var(--l-cream-2)] max-w-xl leading-relaxed">
              Tell us where you&rsquo;re going, what you love, and what you can&rsquo;t stand.
              We&rsquo;ll find the room — and take care of the details, from arrival to the last espresso.
            </p>

            {/* Invitation input */}
            <div className="lobby-reveal d3 mt-10 max-w-xl">
              <div
                className="l-input flex items-center gap-3 px-5 py-4 cursor-text group"
                onClick={() => onStart()}
                data-testid="landing-main-chat-input"
              >
                <MessageCircle className="w-5 h-5 text-[color:var(--l-copper)] shrink-0" />
                <input
                  type="text"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') onStart(); }}
                  placeholder="A quiet room in Lisbon, two nights, arriving Friday…"
                  className="flex-1 bg-transparent outline-none text-base text-[color:var(--l-cream)] placeholder:text-[color:var(--l-cream-3)]"
                />
                <Mic className="w-5 h-5 text-[color:var(--l-cream-3)] group-hover:text-[color:var(--l-copper)] transition-colors" />
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {[
                  { k: 'biz',     label: 'Business trip',    tid: 'quick-action-business' },
                  { k: 'weekend', label: 'Weekend getaway',  tid: 'quick-action-weekend' },
                  { k: 'family',  label: 'Family vacation',  tid: 'quick-action-family' },
                  { k: 'meeting', label: 'Meeting rooms',    tid: 'quick-action-meeting-rooms' },
                ].map((p) => (
                  <button
                    key={p.k}
                    onClick={() => onStart(p.k)}
                    data-testid={p.tid}
                    className="l-btn-ghost px-4 py-2 text-[13px]"
                  >
                    {p.label}
                  </button>
                ))}
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-[13px] text-[color:var(--l-cream-3)]">
                <span className="inline-flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-[color:var(--l-copper)]" /> 30-second booking</span>
                <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> Pay at check-in or with crypto</span>
                <span className="inline-flex items-center gap-1.5"><KeyRound className="w-3.5 h-3.5" /> Skip the front desk</span>
              </div>
            </div>
          </div>

          {/* Right — the one hero photo */}
          <div className="lg:col-span-5 lobby-reveal d2">
            <div className="relative aspect-[4/5] rounded-3xl overflow-hidden border border-[color:var(--l-line)]">
              <img
                src={HERO_PHOTO}
                alt="A quiet, beautifully lit hotel room"
                className="lobby-photo w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[color:var(--l-bg)] via-transparent to-transparent opacity-80" />
              <div className="absolute left-5 right-5 bottom-5 flex items-end justify-between gap-4">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--l-copper)]">Now featured</div>
                  <div className="l-display text-xl sm:text-2xl mt-1">Grand Marina · Lisbon</div>
                  <div className="text-[13px] text-[color:var(--l-cream-2)] mt-0.5">Verified · from $214 / night</div>
                </div>
                <button
                  onClick={() => onStart('hotel_grand_marina')}
                  className="l-btn-primary h-10 px-5 text-sm inline-flex items-center gap-1.5"
                  data-testid="hero-featured-cta"
                >
                  Open <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────  THREE PROMISES (hospitality-framed)  ───────────────────────── */
function Promises() {
  const items = [
    {
      icon: MessageCircle,
      kicker: 'Book by chat',
      title: 'Thirty seconds. No forms.',
      body: 'Describe the trip in your words. Our AI concierge finds the room, the rate, and the extras — and holds the booking while you decide.',
    },
    {
      icon: KeyRound,
      kicker: 'Check in like a guest, not a customer',
      title: 'No counter. No card swipe.',
      body: 'Your room is ready when you arrive. The minibar bills itself quietly in the background — no tab to settle, no receipt to lose.',
    },
    {
      icon: Sparkles,
      kicker: 'Pay the way you want',
      title: 'Card, wire, or tokens — same room.',
      body: 'Stripe on one side, $HERMES on the other. Guests who pay in the network save on every stay and earn on every referral.',
    },
  ];

  return (
    <section className="py-20 sm:py-28 border-t border-[color:var(--l-line)]" data-testid="promises-section">
      <div className="max-w-[1320px] mx-auto px-5 sm:px-8 lg:px-10">
        <div className="max-w-2xl">
          <div className="text-[13px] tracking-wide text-[color:var(--l-cream-3)] mb-3">The short version</div>
          <h2 className="l-display text-4xl sm:text-5xl lg:text-6xl">
            Three things<br />
            feel <span className="text-[color:var(--l-copper)]">different</span>.
          </h2>
        </div>

        <div className="mt-14 grid md:grid-cols-3 gap-8 lg:gap-12">
          {items.map(({ icon: Icon, kicker, title, body }, i) => (
            <div key={title} className="lobby-reveal" style={{ animationDelay: `${120 + i * 120}ms` }}>
              <div className="w-11 h-11 rounded-full bg-[color:var(--l-bg-2)] border border-[color:var(--l-line-2)] flex items-center justify-center">
                <Icon className="w-5 h-5 text-[color:var(--l-copper)]" />
              </div>
              <div className="mt-6 text-[12px] tracking-[0.18em] uppercase text-[color:var(--l-cream-3)]">{kicker}</div>
              <h3 className="l-display text-[26px] sm:text-[28px] mt-2">{title}</h3>
              <p className="text-[15px] text-[color:var(--l-cream-2)] mt-3 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────  FEATURED HOTELS — warm, full colour  ───────────────────────── */
function FeaturedStays({ hotels, onOpen }) {
  if (!hotels || hotels.length === 0) return null;

  return (
    <section className="py-20 sm:py-28 border-t border-[color:var(--l-line)]" data-testid="featured-hotels-section">
      <div className="max-w-[1320px] mx-auto px-5 sm:px-8 lg:px-10">
        <div className="flex items-end justify-between gap-8 flex-wrap">
          <div>
            <div className="text-[13px] tracking-wide text-[color:var(--l-cream-3)] mb-3">Hand-picked</div>
            <h2 className="l-display text-4xl sm:text-5xl lg:text-6xl">Stays worth talking about.</h2>
          </div>
          <Link to="/marketplace" data-testid="featured-browse-link">
            <button className="l-btn-ghost h-11 px-5 text-sm inline-flex items-center gap-2">
              Browse all <ArrowRight className="w-4 h-4" />
            </button>
          </Link>
        </div>

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {hotels.slice(0, 6).map((h) => (
            <div
              key={h.id}
              onClick={() => onOpen(h.id)}
              data-testid="featured-hotel-card"
              className="l-card cursor-pointer group"
            >
              <div className="relative aspect-[4/3] overflow-hidden">
                <img
                  src={h.hero_photo_serve ? `${BACKEND_URL}/api/photos/serve/${h.hero_photo_serve}` : (h.image_url || `https://picsum.photos/seed/${h.id}/900/600`)}
                  alt={h.name}
                  className="lobby-photo w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-[600ms]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[color:var(--l-bg-2)]/70 via-transparent to-transparent opacity-70" />
                {h.verification_status === 'verified' && (
                  <div className="absolute top-4 left-4 text-[11px] tracking-wide text-[color:var(--l-bg)] bg-[color:var(--l-cream)]/92 px-2.5 py-1 rounded-full inline-flex items-center gap-1.5">
                    <ShieldCheck className="w-3 h-3" /> Verified
                  </div>
                )}
              </div>

              <div className="p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="l-display text-xl truncate">{h.name}</h3>
                    <div className="flex items-center gap-1.5 text-[13px] text-[color:var(--l-cream-3)] mt-1">
                      <MapPin className="w-3.5 h-3.5" /> {h.city}, {h.country}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[11px] uppercase tracking-[0.16em] text-[color:var(--l-cream-3)]">Trust</div>
                    <div className="l-display text-lg text-[color:var(--l-copper)] tabular-nums">{h.trust_score}<span className="text-[color:var(--l-cream-3)] text-xs">/100</span></div>
                  </div>
                </div>

                <p className="text-[14px] text-[color:var(--l-cream-2)] mt-3 leading-relaxed line-clamp-2">{h.description}</p>

                <div className="mt-5 pt-4 border-t border-[color:var(--l-line)] flex items-center justify-between text-[13px]">
                  <span className="text-[color:var(--l-cream-3)]">Chat to book</span>
                  <span className="text-[color:var(--l-copper)] inline-flex items-center gap-1.5 group-hover:translate-x-0.5 transition-transform">
                    <MessageCircle className="w-4 h-4" /> Open
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────  UNDER THE HOOD — soft technical proof  ───────────────────────── */
function UnderTheHood() {
  return (
    <section className="py-24 sm:py-32 border-t border-[color:var(--l-line)] relative overflow-hidden" data-testid="moat-section">
      <div className="absolute inset-0 lobby-glow opacity-70 pointer-events-none" />
      <div className="lobby-grain" />

      <div className="max-w-[1320px] mx-auto px-5 sm:px-8 lg:px-10 relative z-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-6">
            <div className="text-[13px] tracking-wide text-[color:var(--l-cream-3)] mb-3">Under the hood</div>
            <h2 className="l-display text-4xl sm:text-5xl lg:text-6xl" data-testid="moat-title">
              Snap two trays.<br />
              <span className="text-[color:var(--l-copper)]">A minibar appears.</span>
            </h2>
            <p className="mt-6 text-lg text-[color:var(--l-cream-2)] max-w-lg leading-relaxed">
              Every tray is a 6-slot weighing scale. Magnetically snap two together for twelve.
              Three for eighteen. When a guest picks something up, the tray quietly asks — no rush, put it back if you&rsquo;re just browsing.
              Confirm, and the charge is posted. Walk away, and housekeeping gets a gentle nudge at checkout.
            </p>
            <p className="mt-4 text-[15px] text-[color:var(--l-cream-3)] max-w-lg leading-relaxed">
              Six layers of shrinkage defence built in. Ambient-light dim at night so the room stays dark.
              Every gram, timestamped and on-chain. The minibar that bills itself — without feeling like it&rsquo;s watching.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/why-getbitsy" data-testid="landing-why-getbitsy-cta">
                <button className="l-btn-ghost h-11 px-5 text-sm inline-flex items-center gap-2">
                  How it works <ArrowRight className="w-4 h-4" />
                </button>
              </Link>
              <Link to="/marina/restock" data-testid="landing-restock-cta">
                <button className="l-btn-ghost h-11 px-5 text-sm inline-flex items-center gap-2">
                  See the operator view <ArrowRight className="w-4 h-4" />
                </button>
              </Link>
            </div>
          </div>

          <div className="lg:col-span-6">
            <div className="l-card p-7 sm:p-9">
              <div className="flex items-center gap-2 text-[12px] tracking-[0.18em] uppercase text-[color:var(--l-cream-3)]">
                <Scale className="w-3.5 h-3.5" /> Proof, not promise
              </div>

              <div className="mt-5 grid grid-cols-3 gap-5">
                {[
                  { l: 'Per slot',      v: '±1g',   sub: 'NAU7802 24-bit' },
                  { l: 'Browse window', v: '60s',   sub: 'No-pressure confirm' },
                  { l: 'Snap-grow',     v: '6→48',  sub: 'Slots per group' },
                ].map((x) => (
                  <div key={x.l}>
                    <div className="text-[11px] uppercase tracking-[0.16em] text-[color:var(--l-cream-3)]">{x.l}</div>
                    <div className="l-display text-2xl sm:text-3xl mt-1 tabular-nums">{x.v}</div>
                    <div className="text-[12px] text-[color:var(--l-cream-3)] mt-0.5">{x.sub}</div>
                  </div>
                ))}
              </div>

              <div className="mt-7 pt-6 border-t border-[color:var(--l-line)]">
                <div className="text-[15px] text-[color:var(--l-cream-2)] leading-relaxed">
                  Six-slot dry trays, magnetically snapped. Every tray runs its own brain — single SKU, zero master/slave, any tray works alone.
                  An acrylic-embedded load cell under every slot. A warm push when you lift something, never a demand.
                  <span className="text-[color:var(--l-cream)]"> We call it the M.O.A.T.</span>
                </div>
                <div className="mt-4 text-[12px] text-[color:var(--l-cream-3)]">
                  Material of Attested Truth — the quiet piece of hardware that lets the room run itself, with no shrinkage left unexplained.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────  HOTELOGRAM (warm reframe)  ───────────────────────── */
function Hotelogram() {
  const cards = [
    { icon: Share2,  title: 'Share the hotels you love',      body: 'Every stay you&apos;ve had gets a unique, warm little referral link. No banners. No begging.' },
    { icon: Coins,   title: 'Earn a small share when they book',body: 'Up to 10% back in $HERMES on referrals — redeemable against your next night, or kept.' },
    { icon: Flame,   title: 'Catch flash rates',                 body: 'Hotels offer 2–5× multipliers to fill quiet weekdays. Invite a friend, pocket the rest.' },
    { icon: Sparkles,title: 'Pretty cards, ready to share',      body: 'We auto-generate the image. You just pick which hotel and hit send.' },
  ];

  return (
    <section className="py-20 sm:py-28 border-t border-[color:var(--l-line)]" data-testid="hotelogram-cta-section">
      <div className="max-w-[1320px] mx-auto px-5 sm:px-8 lg:px-10 grid lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5">
          <div className="text-[13px] tracking-wide text-[color:var(--l-cream-3)] mb-3">Hotelogram</div>
          <h2 className="l-display text-4xl sm:text-5xl lg:text-6xl">
            Good taste,<br />
            <span className="text-[color:var(--l-copper)]">rewarded</span>.
          </h2>
          <p className="mt-6 text-[color:var(--l-cream-2)] max-w-md leading-relaxed">
            The places you&rsquo;d recommend anyway — now give a little back when you do.
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Link to="/guest-login" data-testid="hotelogram-join-cta">
              <button className="l-btn-copper h-11 px-6 text-sm inline-flex items-center gap-2">
                <ArrowRight className="w-4 h-4" /> Join Hotelogram
              </button>
            </Link>
            <Link to="/bounty-board" data-testid="bounty-board-cta">
              <button className="l-btn-ghost h-11 px-6 text-sm inline-flex items-center gap-2">
                <Flame className="w-4 h-4" /> Flash rates
              </button>
            </Link>
          </div>
        </div>

        <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
          {cards.map(({ icon: Icon, title, body }, i) => (
            <div key={title} className="l-card p-6" style={{ animationDelay: `${120 * i}ms` }}>
              <Icon className="w-5 h-5 text-[color:var(--l-copper)]" />
              <div className="l-display text-[18px] mt-4">{title}</div>
              <div
                className="text-[14px] text-[color:var(--l-cream-2)] mt-2 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: body }}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────  OWNER CTA  ───────────────────────── */
function OwnerCta() {
  return (
    <section className="py-24 sm:py-32 border-t border-[color:var(--l-line)] relative overflow-hidden" data-testid="owner-cta-section">
      <div className="absolute inset-0 lobby-glow pointer-events-none" />
      <div className="max-w-3xl mx-auto px-5 relative z-10 text-center">
        <div className="text-[13px] tracking-wide text-[color:var(--l-cream-3)] mb-3">For hotels</div>
        <h2 className="l-display text-4xl sm:text-5xl lg:text-6xl">
          Run the front desk<br />
          from <span className="text-[color:var(--l-copper)]">a conversation</span>.
        </h2>
        <p className="text-[color:var(--l-cream-2)] mt-6 leading-relaxed max-w-xl mx-auto text-lg">
          We install the quiet hardware, deploy four AI agents to handle bookings, housekeeping, pricing and audit — and settle payments the way your guests prefer.
          <span className="block mt-2 text-[color:var(--l-cream)]">6% commission. 1% flows back to the network your hotel now belongs to.</span>
        </p>
        <div className="flex flex-wrap gap-3 justify-center mt-10">
          <Link to="/register">
            <button className="l-btn-primary h-12 px-8 text-sm inline-flex items-center gap-2">
              Get started <ArrowRight className="w-4 h-4" />
            </button>
          </Link>
          <Link to="/why-getbitsy">
            <button className="l-btn-ghost h-12 px-8 text-sm inline-flex items-center gap-2">
              Compare to traditional PMS
            </button>
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────  FOOTER  ───────────────────────── */
function Footer() {
  return (
    <footer className="border-t border-[color:var(--l-line)] py-10" data-testid="landing-footer">
      <div className="max-w-[1320px] mx-auto px-5 sm:px-8 lg:px-10 grid sm:grid-cols-3 items-center gap-6">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-full bg-[color:var(--l-copper)] flex items-center justify-center">
            <span className="l-display text-[color:var(--l-bg)] text-xs">b</span>
          </div>
          <span className="l-display text-[15px]">getbitsy</span>
        </div>

        <div className="flex items-center justify-center gap-6 text-sm text-[color:var(--l-cream-3)]">
          <Link to="/marketplace" className="hover:text-[color:var(--l-cream)] transition-colors">Stays</Link>
          <Link to="/guest-login" className="hover:text-[color:var(--l-cream)] transition-colors">Hotelogram</Link>
          <Link to="/why-getbitsy" className="hover:text-[color:var(--l-cream)] transition-colors">How it works</Link>
          <Link to="/docs" className="hover:text-[color:var(--l-cream)] transition-colors" data-testid="footer-docs">Docs</Link>
          <Link to="/bounty-board" className="hover:text-[color:var(--l-cream)] transition-colors">Rewards</Link>
        </div>

        <div className="text-right text-[12px] text-[color:var(--l-cream-3)]">
          <span className="inline-flex items-center gap-2">
            <Leaf className="w-3.5 h-3.5 text-[color:var(--l-copper)]" /> Hospitality, quietly rebuilt.
          </span>
        </div>
      </div>
    </footer>
  );
}

/* ─────────────────────────  PAGE  ───────────────────────── */
export default function LandingPage() {
  const navigate = useNavigate();
  const [hotelsList, setHotelsList] = useState([]);

  useEffect(() => {
    seed().catch(() => {});
    hotelsApi.listPublic().then(({ data }) => setHotelsList(data)).catch(() => {});
  }, []);

  const handleStartBooking = async (hotelIdOrPreset) => {
    try { await seed(); } catch (e) {}
    const id = typeof hotelIdOrPreset === 'string' && hotelIdOrPreset.startsWith('hotel_')
      ? hotelIdOrPreset
      : 'hotel_grand_marina';
    navigate(`/book/${id}`);
  };

  return (
    <div className="lobby-theme min-h-screen" data-testid="landing-page">
      <SEO
        title="getbitsy.ai — The Agentic Hospitality OS · Direct Booking Engine + Hotel AI Agents"
        description="getbitsy.ai is the Agentic Hospitality OS: a direct booking engine, autonomous Hotel AI Agents (Bitsy, Marina, Sage, Argus, Hermes), and middleman-free hospitality on the BHERMES Protocol. 5%+1% — 1% flows back to operators."
        canonical="https://getbitsy.ai/"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: 'getbitsy.ai',
          alternateName: 'Agentic Hospitality OS',
          url: 'https://getbitsy.ai',
          potentialAction: {
            '@type': 'SearchAction',
            target: 'https://getbitsy.ai/?q={search_term_string}',
            'query-input': 'required name=search_term_string',
          },
        }}
      />
      <LobbyNav />
      <Hero onStart={handleStartBooking} />
      <Promises />
      <FeaturedStays hotels={hotelsList} onOpen={handleStartBooking} />
      <UnderTheHood />
      <Hotelogram />
      <OwnerCta />
      <Footer />
    </div>
  );
}
